import {sendAmplitudeEvent} from '#/analytics/amplitude-events';
import environment from '#/environment/environment';
import {useSafeWebSocketPublicClient} from '#/hooks/use-safe-websocket-public-client';
import {useWeb3AuthSignatureGroup} from '#/hooks/use-web3auth-signature-group';
import {formatNumber} from '#/utils/number-utils';
import {DefaultChildren} from '#/utils/utils';
import {CustomWeb3AuthConnector} from '#/wagmi/create-custom-web3auth-connector';
import {web3AuthInstance} from '#/wagmi/wagmi-config';
import {WALLET_ADAPTER_TYPE} from '@web3auth/base';
import {Web3AuthNoModal} from '@web3auth/no-modal';
import {OpenloginLoginParams} from '@web3auth/openlogin-adapter';
import Cookies from 'js-cookie';
import {FunctionComponent, createContext, useContext, useMemo} from 'react';
import {erc20Abi} from 'viem';
import {
  UseAccountReturnType,
  UsePublicClientReturnType,
  UseWalletClientReturnType,
  useAccount,
  useConfig,
  useConnect,
  useDisconnect,
  usePublicClient,
  useReadContracts,
  useWalletClient,
} from 'wagmi';

interface Balance {
  decimals: number;
  symbol: string;
  formattedValue: string;
  value: number;
}

export interface WalletContextValue {
  accountResult: UseAccountReturnType;
  publicClient: UsePublicClientReturnType;
  webSocketPublicClient: UsePublicClientReturnType | undefined;
  walletClient: UseWalletClientReturnType | undefined;
  connectWithWeb3Auth: (loginParams: {walletName: WALLET_ADAPTER_TYPE; loginParams?: OpenloginLoginParams}) => void;
  logout: () => void;
  web3Auth: Web3AuthNoModal | null;
  web3AuthSignatureResult: ReturnType<typeof useWeb3AuthSignatureGroup>;
  usdCBalance: Balance;
  refetchWalletBalance: () => void;
}

const USDC_CONTRACT = environment.usdcContract;
const initialValue: WalletContextValue = {} as WalletContextValue;

export const WalletContext = createContext<WalletContextValue>(initialValue);

export const WalletProvider: FunctionComponent<DefaultChildren> = ({children}) => {
  const {chains, connectors} = useConfig();
  const web3AuthConnector = connectors[0] as CustomWeb3AuthConnector;

  const {connect} = useConnect();
  const {disconnect} = useDisconnect();

  const accountResult = useAccount();

  const publicClient = usePublicClient();
  const webSocketPublicClient = useSafeWebSocketPublicClient(publicClient, accountResult.chain);
  const walletClient = useWalletClient({
    account: accountResult.address,
  });

  const web3AuthSignatureResult = useWeb3AuthSignatureGroup(web3AuthInstance, accountResult);

  const connectWithWeb3Auth: WalletContextValue['connectWithWeb3Auth'] = loginParams => {
    Cookies.set('referrer', document.referrer, {expires: 1, path: '/'});

    web3AuthConnector.setAdapter(loginParams.walletName, loginParams.loginParams);
    connect({
      chainId: chains[0].id,
      connector: web3AuthConnector,
    });
  };

  const result = useReadContracts({
    allowFailure: false,
    contracts: [
      {
        address: USDC_CONTRACT as `0x${string}`,
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [accountResult.address as `0x${string}`],
      },
      {
        address: USDC_CONTRACT as `0x${string}`,
        functionName: 'decimals',
        abi: erc20Abi,
      },
      {
        address: USDC_CONTRACT as `0x${string}`,
        functionName: 'symbol',
        abi: erc20Abi,
      },
    ],
  });

  const usdCBalance = useMemo(() => {
    if (result?.data && result.data.length > 0) {
      const value = Number(result.data[0]) / 10 ** result.data[1];
      sendAmplitudeEvent.walletBalance({event_properties: {wallet_balance: value}});
      const formattedValue = formatNumber(value);
      return {
        symbol: result.data[2],
        decimals: result.data[1],
        formattedValue,
        value,
      };
    }

    return {} as Balance;
  }, [result]);

  return (
    <WalletContext.Provider
      value={{
        accountResult,
        usdCBalance,
        publicClient,
        webSocketPublicClient,
        walletClient,
        connectWithWeb3Auth,
        logout: disconnect,
        web3Auth: web3AuthInstance,
        web3AuthSignatureResult,
        refetchWalletBalance: result?.refetch,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
};

export const useWalletProvider = () => {
  if (!WalletContext) {
    throw new Error('WalletContet is undefined');
  }
  return useContext(WalletContext);
};
